import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DoubleSide } from 'three';

import { localStorageUtils } from '../../../../../../../utils/local-storage-utils';
import { Design } from '../../../../../../../viewer/elements/design';
import { publish, SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import { DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { clamp } from '../../../../../../../viewer/utilities/math-helpers';
import { useAssets } from '../../../../../../hooks/use-assets';
import { useFeatureFlag } from '../../../../../../hooks/use-feature-flag';
import { useSubscribe } from '../../../../../../hooks/use-subscribe';
import { CheckBox } from '../../../../../check-box/check-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { Slider } from '../../../../../slider/slider';
import { Stack } from '../../../../../stack/stack';
import { Tooltip } from '../../../../../tooltip/tooltip';
import { CollapsableSection } from '../../../components/collapsable-section';
import { OptionLabels } from '../../../components/option-labels';

type Props = {
  expanded: boolean;
  tool: DesignTool;
  tooltipInMenu: boolean;
  lightTooltip: boolean;
  onTooltipInMenuChanged: (value: boolean) => void;
  onLightTooltipChanged: (value: boolean) => void;
  /** Shows all heights at the snapped point, see HeightLayersGuide. */
  allHeights: boolean;
  onAllHeightsChanged: (value: boolean) => void;
};

const minMultiplierValue = 0.2;

export const OptionsSection = (props: Props) => {
  const { t } = useTranslation();
  const tooltipInMenuEnabled = useFeatureFlag('SK-7540-tooltip-in-menu');
  const allHeightsEnabled = useFeatureFlag('SK-7558-visualize-all-heights-option');
  const flexibleSnappingEnabled = useFeatureFlag('SK-7512-flexible-snapping');
  const designCurvesEnabled = useFeatureFlag('SK-7535-design-curves');
  const curveMeasureEnabled = flexibleSnappingEnabled && designCurvesEnabled;
  const [curveMeasure, setCurveMeasure] = useState(props.tool.curveMeasure);
  const [doubleSide, setDoubleSide] = useState(props.tool.asset.side === DoubleSide);
  const [selectOptions, setSelectOptions] = useState(props.tool.selectOptions);
  const { assets } = useAssets(['design']);
  const [pointSizeMultiplier, setPointSizeMultiplier] = useState(
    clamp(
      localStorageUtils.getNumericUserItemOrDefault('designToolPointSizeMultiplier', 1),
      minMultiplierValue,
      1,
    ),
  );

  useEffect(() => {
    for (const design of assets) {
      if (design instanceof Design) {
        design.setPointSize(design.locked, pointSizeMultiplier);
      }
    }
    props.tool.asset.setPointSize(true, pointSizeMultiplier);
    localStorageUtils.setUserItem('designToolPointSizeMultiplier', pointSizeMultiplier.toString());
  }, [pointSizeMultiplier, assets, props.tool.asset]);

  useEffect(() => {
    props.tool.onSelectOptionsChanged = (newOptions) => {
      setSelectOptions(newOptions);
    };

    return () => {
      props.tool.onSelectOptionsChanged = undefined;
    };
  }, [props]);

  useSubscribe(SubscriptionTopic.DesignDoubleSideChanged, () => {
    setDoubleSide(props.tool.asset.side === DoubleSide);
  });

  return (
    <CollapsableSection
      expanded={props.expanded}
      title={t('section.options.title', { ns: 'skyviewDesign' }).toUpperCase()}
      onSetExpanded={(state) =>
        publish(SubscriptionTopic.DesignToolMenuStateChanged, { options: state })
      }
    >
      <Stack spacing={0.8}>
        <LabelledContainer text={t('section.options.pointSize', { ns: 'skyviewDesign' })}>
          <Slider
            max={1}
            min={minMultiplierValue}
            step={0.01}
            value={pointSizeMultiplier}
            onChange={setPointSizeMultiplier}
          />
        </LabelledContainer>

        <Tooltip
          content={t('section.options.doubleSidedMode.tooltip.text', { ns: 'skyviewDesign' })}
          mainAxisOffset={22}
          placement="left"
          title={t('section.options.doubleSidedMode.tooltip.title', { ns: 'skyviewDesign' })}
          wrapChildren={true}
        >
          <CheckBox checked={doubleSide} onChange={() => props.tool.asset.toggleDoubleSided()}>
            {t('section.options.doubleSidedMode.text', { ns: 'skyviewDesign' })}
          </CheckBox>
        </Tooltip>

        <Tooltip
          content={
            <Trans
              components={{ br: <br />, bold: <strong /> }}
              i18nKey="section.options.multiSelection.tooltip.text"
              ns="skyviewDesign"
            />
          }
          mainAxisOffset={22}
          placement="left"
          title={t('section.options.multiSelection.tooltip.title', { ns: 'skyviewDesign' })}
          wrapChildren={true}
        >
          <LabelledContainer
            text={t('section.options.multiSelection.title', { ns: 'skyviewDesign' })}
          >
            <Stack spacing={0.4}>
              <CheckBox
                checked={selectOptions.points}
                onChange={() => props.tool.setSelectOptions({ points: !selectOptions.points })}
              >
                {t('section.options.multiSelection.point', { ns: 'skyviewDesign' })}
              </CheckBox>
              <CheckBox
                checked={selectOptions.lines}
                onChange={() => props.tool.setSelectOptions({ lines: !selectOptions.lines })}
              >
                {t('section.options.multiSelection.lines', { ns: 'skyviewDesign' })}
              </CheckBox>
              <CheckBox
                checked={selectOptions.breakLines}
                onChange={() =>
                  props.tool.setSelectOptions({ breakLines: !selectOptions.breakLines })
                }
              >
                {t('section.options.multiSelection.breakLines', { ns: 'skyviewDesign' })}
              </CheckBox>
              <CheckBox
                checked={selectOptions.faces}
                onChange={() => props.tool.setSelectOptions({ faces: !selectOptions.faces })}
              >
                {t('section.options.multiSelection.triangles', { ns: 'skyviewDesign' })}
              </CheckBox>
            </Stack>
          </LabelledContainer>
        </Tooltip>

        {curveMeasureEnabled && (
          <Tooltip
            content={t('section.options.curveMeasure.tooltip.text', { ns: 'skyviewDesign' })}
            mainAxisOffset={22}
            placement="left"
            title={t('section.options.curveMeasure.tooltip.title', { ns: 'skyviewDesign' })}
            wrapChildren={true}
          >
            <LabelledContainer
              text={t('section.options.curveMeasure.title', { ns: 'skyviewDesign' })}
            >
              <OptionLabels
                active={curveMeasure}
                itemRows={[
                  [
                    {
                      title: t('section.options.curveMeasure.radius', { ns: 'skyviewDesign' }),
                      value: 'radius' as const,
                    },
                    {
                      title: t('section.options.curveMeasure.bulge', { ns: 'skyviewDesign' }),
                      value: 'bulge' as const,
                    },
                  ].map((item) => ({
                    ...item,
                    onSelect: () => {
                      props.tool.curveMeasure = item.value;
                      setCurveMeasure(item.value);
                    },
                  })),
                ]}
              />
            </LabelledContainer>
          </Tooltip>
        )}

        {(tooltipInMenuEnabled || allHeightsEnabled) && (
          <LabelledContainer
            text={t('section.options.tooltipInMenu.title', { ns: 'skyviewDesign' })}
          >
            <Stack spacing={0.4}>
              {tooltipInMenuEnabled && (
                <Tooltip
                  content={t('section.options.tooltipInMenu.tooltip.text', {
                    ns: 'skyviewDesign',
                  })}
                  mainAxisOffset={22}
                  placement="left"
                  title={t('section.options.tooltipInMenu.tooltip.title', { ns: 'skyviewDesign' })}
                  wrapChildren={true}
                >
                  <Stack spacing={0.4}>
                    <CheckBox
                      checked={props.tooltipInMenu}
                      onChange={() => props.onTooltipInMenuChanged(!props.tooltipInMenu)}
                    >
                      {t('section.options.tooltipInMenu.text', { ns: 'skyviewDesign' })}
                    </CheckBox>
                    <CheckBox
                      checked={props.lightTooltip}
                      disabled={!props.tooltipInMenu}
                      onChange={() => props.onLightTooltipChanged(!props.lightTooltip)}
                    >
                      {t('section.options.tooltipInMenu.light', { ns: 'skyviewDesign' })}
                    </CheckBox>
                  </Stack>
                </Tooltip>
              )}
              {allHeightsEnabled && (
                <Tooltip
                  content={t('section.options.allHeights.tooltip.text', { ns: 'skyviewDesign' })}
                  mainAxisOffset={22}
                  placement="left"
                  title={t('section.options.allHeights.tooltip.title', { ns: 'skyviewDesign' })}
                  wrapChildren={true}
                >
                  <CheckBox
                    checked={props.allHeights}
                    onChange={() => props.onAllHeightsChanged(!props.allHeights)}
                  >
                    {t('section.options.allHeights.text', { ns: 'skyviewDesign' })}
                  </CheckBox>
                </Tooltip>
              )}
            </Stack>
          </LabelledContainer>
        )}
      </Stack>
    </CollapsableSection>
  );
};
